import React from 'react';

const LeadsStats = ({ stats, userTier = 'free', onRefresh }) => {
  const formatNumber = (num) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num?.toLocaleString() || '0';
  };



  const getTierStats = () => {
    if (!stats?.leads) return null;

    switch (userTier) {
      case 'free':
        return {
          total: stats.leads.free_leads || 0,
          label: 'Free Leads',
          color: '#22C55E'
        };
      case 'starter':
        return {
          total: (stats.leads.free_leads || 0) + (stats.leads.starter_leads || 0),
          label: 'Available Leads',
          color: '#3B82F6'
        };
      case 'pro':
        return {
          total: (stats.leads.free_leads || 0) + (stats.leads.starter_leads || 0) + (stats.leads.pro_leads || 0),
          label: 'Available Leads',
          color: '#A855F7'
        };
      default:
        return {
          total: stats.leads.free_leads || 0,
          label: 'Free Leads',
          color: '#22C55E'
        };
    }
  };

  const tierStats = getTierStats();

  const styles = {
    container: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
      gap: '24px',
      marginBottom: '32px',
      animation: 'fadeInUp 0.5s ease forwards'
    },
    statCard: {
      background: 'rgba(255, 255, 255, 0.05)',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      borderRadius: '16px',
      padding: '24px',
      backdropFilter: 'blur(10px)',
      transition: 'all 0.3s ease',
      cursor: 'pointer',
      animation: 'fadeInUp 0.5s ease forwards',
      animationDelay: '0.1s'
    },
    statHeader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: '16px'
    },
    statIcon: {
      width: '48px',
      height: '48px',
      borderRadius: '12px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '24px'
    },
    statValue: {
      fontSize: '32px',
      fontWeight: '800',
      color: '#ffffff',
      marginBottom: '8px',
      lineHeight: '1'
    },
    statLabel: {
      fontSize: '14px',
      color: 'rgba(255, 255, 255, 0.7)',
      fontWeight: '500',
      textTransform: 'uppercase',
      letterSpacing: '0.5px'
    },
    statSubtext: {
      fontSize: '12px',
      color: 'rgba(255, 255, 255, 0.5)',
      marginTop: '8px'
    },
    progressBar: {
      width: '100%',
      height: '6px',
      background: 'rgba(255, 255, 255, 0.1)',
      borderRadius: '3px',
      overflow: 'hidden',
      marginTop: '12px'
    },
    progressFill: {
      height: '100%',
      borderRadius: '3px',
      transition: 'width 0.3s ease'
    },
    tierBreakdown: {
      display: 'flex',
      flexDirection: 'column',
      gap: '12px',
      marginTop: '16px'
    },
    tierItem: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '8px 12px',
      background: 'rgba(255, 255, 255, 0.05)',
      borderRadius: '8px',
      fontSize: '14px'
    },
    tierLabel: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      color: 'rgba(255, 255, 255, 0.8)'
    },
    tierCount: {
      color: '#ffffff',
      fontWeight: '600'
    },
    tierDot: {
      width: '8px',
      height: '8px',
      borderRadius: '50%'
    },
    refreshButton: {
      position: 'absolute',
      top: '16px',
      right: '16px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '8px 12px',
      background: 'rgba(255, 255, 255, 0.1)',
      border: '1px solid rgba(255, 255, 255, 0.2)',
      borderRadius: '8px',
      color: '#ffffff',
      fontSize: '12px',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      '&:hover': {
        background: 'rgba(255, 255, 255, 0.15)',
        transform: 'rotate(180deg)'
      },
      '&:focus': {
        outline: '2px solid #3B82F6',
        outlineOffset: '2px'
      }
    },
    chartContainer: {
      background: 'rgba(255, 255, 255, 0.05)',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      borderRadius: '16px',
      padding: '24px',
      backdropFilter: 'blur(10px)',
      marginBottom: '24px',
      animation: 'fadeInUp 0.5s ease forwards',
      animationDelay: '0.3s'
    },
    chartTitle: {
      fontSize: '18px',
      fontWeight: '600',
      color: '#ffffff',
      marginBottom: '20px'
    },
    chartGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '16px'
    },
    chartItem: {
      textAlign: 'center',
      padding: '16px',
      background: 'rgba(255, 255, 255, 0.05)',
      borderRadius: '12px',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      transition: 'all 0.3s ease',
      '&:hover': {
        background: 'rgba(255, 255, 255, 0.08)',
        transform: 'translateY(-2px)',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
      }
    },
    chartValue: {
      fontSize: '24px',
      fontWeight: '700',
      color: '#3B82F6',
      marginBottom: '4px'
    },
    chartLabel: {
      fontSize: '12px',
      color: 'rgba(255, 255, 255, 0.7)',
      fontWeight: '500'
    }
  };

  if (!stats) {
    return (
      <div style={styles.container}>
        <div style={styles.statCard}>
          <div style={{ textAlign: 'center', color: 'rgba(255, 255, 255, 0.7)' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>📊</div>
            <h3>Loading stats...</h3>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ position: 'relative' }}>
      {/* Refresh Button */}
      <button style={styles.refreshButton} onClick={onRefresh}>
        🔄 Refresh
      </button>

      {/* Main Stats Grid */}
      <div style={styles.container}>
        {/* Available Leads */}
        <div 
          style={styles.statCard}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-4px)';
            e.currentTarget.style.boxShadow = '0 20px 40px rgba(0, 0, 0, 0.3)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = 'none';
          }}
        >
          <div style={styles.statHeader}>
            <div style={{ ...styles.statIcon, background: 'rgba(59, 130, 246, 0.2)' }}>
              👥
            </div>
          </div>
          <div style={styles.statValue}>
            {formatNumber(tierStats?.total || 0)}
          </div>
          <div style={styles.statLabel}>
            {tierStats?.label || 'Available Leads'}
          </div>
          <div style={styles.statSubtext}>
            Based on your {userTier} subscription
          </div>
          
          {/* Tier Breakdown */}
          {userTier !== 'free' && (
            <div style={styles.tierBreakdown}>
              <div style={styles.tierItem}>
                <div style={styles.tierLabel}>
                  <div style={{ ...styles.tierDot, background: '#22C55E' }} />
                  Free
                </div>
                <div style={styles.tierCount}>{formatNumber(stats.leads?.free_leads || 0)}</div>
              </div>
              {userTier !== 'free' && (
                <div style={styles.tierItem}>
                  <div style={styles.tierLabel}>
                    <div style={{ ...styles.tierDot, background: '#3B82F6' }} />
                    Starter
                  </div>
                  <div style={styles.tierCount}>{formatNumber(stats.leads?.starter_leads || 0)}</div>
                </div>
              )}
              {userTier === 'pro' && (
                <div style={styles.tierItem}>
                  <div style={styles.tierLabel}>
                    <div style={{ ...styles.tierDot, background: '#A855F7' }} />
                    Pro
                  </div>
                  <div style={styles.tierCount}>{formatNumber(stats.leads?.pro_leads || 0)}</div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Active Installers */}
        <div 
          style={styles.statCard}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-4px)';
            e.currentTarget.style.boxShadow = '0 20px 40px rgba(0, 0, 0, 0.3)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = 'none';
          }}
        >
          <div style={styles.statHeader}>
            <div style={{ ...styles.statIcon, background: 'rgba(34, 197, 94, 0.2)' }}>
              ✅
            </div>
          </div>
          <div style={styles.statValue}>
            {formatNumber(stats.leads?.active_installers || 0)}
          </div>
          <div style={styles.statLabel}>
            Active Installers
          </div>
          <div style={styles.statSubtext}>
            Currently active in the market
          </div>
          <div style={styles.progressBar}>
            <div 
              style={{
                ...styles.progressFill,
                background: '#22C55E',
                width: `${Math.min(((stats.leads?.active_installers || 0) / Math.max(tierStats?.total || 1, 1)) * 100, 100)}%`
              }}
            />
          </div>
        </div>

        {/* Recent Activity */}
        <div 
          style={styles.statCard}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-4px)';
            e.currentTarget.style.boxShadow = '0 20px 40px rgba(0, 0, 0, 0.3)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = 'none';
          }}
        >
          <div style={styles.statHeader}>
            <div style={{ ...styles.statIcon, background: 'rgba(245, 158, 11, 0.2)' }}>
              📈
            </div>
          </div>
          <div style={styles.statValue}>
            {formatNumber(stats.leads?.recent_activity || 0)}
          </div>
          <div style={styles.statLabel}>
            Recent Activity (30 Days)
          </div>
          <div style={styles.statSubtext}>
            Installers with recent installations
          </div>
          <div style={styles.progressBar}>
            <div 
              style={{
                ...styles.progressFill,
                background: '#F59E0B',
                width: `${Math.min(((stats.leads?.recent_activity || 0) / Math.max(tierStats?.total || 1, 1)) * 100, 100)}%`
              }}
            />
          </div>
        </div>

        {/* Average Installations */}
        <div 
          style={styles.statCard}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-4px)';
            e.currentTarget.style.boxShadow = '0 20px 40px rgba(0, 0, 0, 0.3)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = 'none';
          }}
        >
          <div style={styles.statHeader}>
            <div style={{ ...styles.statIcon, background: 'rgba(168, 85, 247, 0.2)' }}>
              🏗️
            </div>
          </div>
          <div style={styles.statValue}>
            {formatNumber(Math.round(stats.leads?.avg_installations_per_lead || 0))}
          </div>
          <div style={styles.statLabel}>
            Avg Installations per Lead
          </div>
          <div style={styles.statSubtext}>
            Average across all leads
          </div>
        </div>
      </div>

      {/* Additional Charts */}
      <div style={styles.chartContainer}>
        <h3 style={styles.chartTitle}>Performance Metrics</h3>
        <div style={styles.chartGrid}>
          <div style={styles.chartItem}>
            <div style={styles.chartValue}>
              {stats.leads?.last_leads_refresh ? 
                new Date(stats.leads.last_leads_refresh).toLocaleDateString() : 
                'N/A'
              }
            </div>
            <div style={styles.chartLabel}>Last Data Refresh</div>
          </div>
          <div style={styles.chartItem}>
            <div style={styles.chartValue}>
              {stats.user?.monthly_exports_limit - stats.user?.monthly_exports_used || 0}
            </div>
            <div style={styles.chartLabel}>Remaining Exports</div>
          </div>
          <div style={styles.chartItem}>
            <div style={styles.chartValue}>
              {stats.user?.subscription_tier?.toUpperCase() || 'FREE'}
            </div>
            <div style={styles.chartLabel}>Your Plan</div>
          </div>
          <div style={styles.chartItem}>
            <div style={styles.chartValue}>
              {formatNumber(stats.leads?.total_leads || 0)}
            </div>
            <div style={styles.chartLabel}>Total Leads in Database</div>
          </div>
        </div>
      </div>

      {/* CSS Animations */}
      <style jsx>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes pulse {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.7;
          }
        }
      `}</style>
    </div>
  );
};

export default LeadsStats; 